import { dcrIconService } from '@diekeure/dcr-components';
// Side-effect import: registers the <dcr-icon> custom element.
import '@diekeure/dcr-components/dcr-icon.js';

/**
 * Loads the remote dcr-icon manifest into the shared dcrIconService. Call from each app's
 * `main.ts` BEFORE bootstrap, so the very first <dcr-icon>s already resolve (dcr-icon resolves
 * an icon once on input and does not retry when the manifest arrives later).
 *
 * @param base Manifest directory or file. Defaults to `icons/manifest.json` resolved against the
 *   app's `<base href>` (→ `/app/icons/...`, `/icons/...`, etc.) — the path each app's dev proxy
 *   forwards to the CDN. Pass an absolute `https://…` base for prod if it isn't same-origin.
 * @returns the load promise (never rejects — failures are logged) so main.ts can await it.
 */
export function loadIconManifest(base?: string): Promise<unknown> {
  const url = resolveIconManifestUrl(base);
  return Promise.resolve(dcrIconService.loadManifest(url)).catch((err) => {
    // eslint-disable-next-line no-console
    console.error('Icon manifest failed to load from', url, err);
  });
}

/**
 * Resolves a manifest `base` (directory or file) to the absolute URL that
 * {@link loadIconManifest} loads. Shared so consumers that read the manifest
 * later (e.g. an icon picker via the icon service) hit the exact same URL and
 * therefore the cached, already-loaded manifest.
 */
export function resolveIconManifestUrl(base?: string): string {
  const manifestPath = base
    ? base.endsWith('.json')
      ? base
      : base.replace(/\/+$/, '') + '/manifest.json'
    : 'icons/manifest.json';
  return /^https?:\/\//i.test(manifestPath) ? manifestPath : new URL(manifestPath, document.baseURI).toString();
}
